import classNames from 'classnames';
import currencyFormatter from 'currency-formatter';
import he from 'he';
import fp from 'lodash/fp';
import React, { useState } from 'react';
import { Button, Row, Col } from 'react-bootstrap';
import cookie from 'react-cookies';
import { connect } from 'react-redux';
import Spinner from 'react-spinkit';
import { compose, lifecycle, withHandlers, withProps, withStateHandlers } from 'recompose';
import { bindActionCreators } from 'redux';
import { links } from '../../../constants';
import { handleInvalidForm, isOrganizationPlan, getDiscountedPrice } from '../../../services';
import { businessActions, businessSelector } from '../../../state/modules/business';
import { stripeConfigSelector } from '../../../state/modules/configuration/selectors';
import { sessionActions } from '../../../state/modules/session';
import { userActions } from '../../../state/modules/user';
import { ControlGroup, ControlOption, CreditCardElements, Icon, NumberFormField, InputFormField } from '../../components';
import { withStripe } from '../../components/higher-order';
import { paymentActions } from '../../../state/modules/payment';

const stripeErrorPrefix = "Stripe Error - ";

const nullToEmptyString = (str) => str ? str : ""

const getMailtoString = (address, subject, cc, bcc, body) => `mailto:${address}?cc=${nullToEmptyString(cc)}&bcc=${nullToEmptyString(bcc)}&subject=${encodeURIComponent(nullToEmptyString(subject))}&body=${encodeURIComponent(nullToEmptyString(body))}`

const Mailto = ({ text, address, subject, cc, bcc, body }) => <a href={getMailtoString(address, subject, cc, bcc, body)}>{text}</a>

const getEmailBody = ({
  name,
  companyName,
  contactEmail,
  companySize
}) => (
    `Hi, Caddie.


I'm setting up a ThinkCaddie account for my business and I'd like some help setting up my users.  My details are:

Name: ${name}; 

Company name: ${companyName}; 

company size: ${companySize}; 

contact email: ${contactEmail}; 

contact number: ??;


Thanks!    
`)

const couponMessage = (coupon) => {
  if (coupon === undefined) {
    return (<p style={{ color: 'red' }}>Cannot found affiliate code.</p>);
  }

  if (!coupon.valid) {
    return (<p style={{ color: 'red' }}>Affiliate code not valid.</p>);
  }

  if (coupon.percent_off !== undefined) {
    return (<p style={{ color: '#1FD1A1' }}>Affiliate code found, applied {coupon.percent_off}% off!</p>) 
  }

  if (coupon.amount_off !== undefined) {
    return (<p style={{ color: '#1FD1A1' }}>Affiliate code found, applied ${coupon.amount_off} off!</p>) 
  }
}

/**
 * In CADDIE-323, we rename the coupon to affiliate code.
 * @see https://complii.atlassian.net/browse/CADDIE-323
 */
const CouponCodeForm = ({
  coupon,
  getCoupon,
  isFetchingCoupon,
}) => {
  const [inputCoupon, setInputCoupon] = useState('');
  const [showCouponMessage, setShowCouponMessage] = useState(false);

  return (
    <div>
      <h2>Do you have affiliate code?</h2>
      <Row className='md-gutter'>
        <Col xs={12} md={8}>
          <InputFormField
            id='coupon-code'
            label='Affiliate Code'
            value={inputCoupon}
            onChange={(e) => {
              setShowCouponMessage(false);
              setInputCoupon(e.target.value)
            }}
          />
        </Col>
        <Col style={{ marginTop: '30px'}}>
          <Button onClick={() => {
            setShowCouponMessage(true);
            getCoupon(inputCoupon)
          }}>
            {
              isFetchingCoupon ? 
              <Spinner name='circle' className='spinner' fadeIn='none' />
              : 'Apply'
            }
          </Button>
        </Col>
      </Row>
      {
        !isFetchingCoupon && showCouponMessage ? couponMessage(coupon) : null
      }
    </div>
  );
};

const CreditCardForm = ({
  className,
  createCardPayment,
  selectedPlan,
  config,
  user,
  userCountProps,
  numberOfUsers,
  price,
  interval,
  invalidUserCount,
  maxUsersExceeded,
  setCardError,
  cardError,
  termsProps,
  readTermsProps,
  isTermsValid,
  disableSubmit,
  logout,
  emailBodyParams,
  checkServerError,
  hideServerError,
  businessError,
  setFormRef,
  showCoupon,
  coupon,
  getCoupon,
  isFetchingCoupon,
}) => (
    <form ref={(form) => setFormRef(form)} className={className} onSubmit={createCardPayment} noValidate>
      <fieldset className='boxed'>
        <h2>{he.decode(selectedPlan.name)} plan</h2>
        {
          isOrganizationPlan(selectedPlan) ?
            <div>
              <h3>How many users in your team?</h3>
              <NumberFormField {...userCountProps} />
              <p><small>You can continue to update this once you're into <br /> Caddie and your account charge will adjust as you go.</small></p>
              <p className='equation'>
                <small>
                  <span className='updateNumber'>
                    {numberOfUsers}</span> x {currencyFormatter.format(getDiscountedPrice(coupon, selectedPlan.unitPrice.minorAmount) / 100, { code: selectedPlan.unitPrice.currencyCode })}
                </small>
              </p>
            </div> :
            null
        }
        <p className='total'>
          <i>$</i>
          <span className='total'>
            {currencyFormatter.format(price, { format: '%v' })}
          </span>
          <span className='note'>per {interval}, ex GST<br />cancel anytime</span>
        </p>
        <p id='users--smallteams'><strong>Your card will not be charged until after your free trial</strong></p>
        {
          invalidUserCount ?
            <div id='users--enterprise'>
              <p className='warning'>
                <Icon icon='warning-large-alt' />
                That's a lot of team members! If you'd like a hand setting up your account, <Mailto text="contact us" address="support@thinkcaddie" subject="Help setting up my Caddie business account." body={getEmailBody(emailBodyParams)} /> for assistance.
              </p>
            </div> :
            null
        }
      </fieldset>
      <fieldset className='payment'>
        {
          showCoupon ?
          <CouponCodeForm coupon={coupon} getCoupon={getCoupon} isFetchingCoupon={isFetchingCoupon} /> :
          null
        }
        <h2>Your payment details</h2>
        <CreditCardElements
          setCardError={setCardError}
          hideServerError={hideServerError}
          expirationPlaceholder='MM / YY'
        />
        {
          cardError ?
            <div aria-invalid='true' className="has-error">
              <span className='help-block'>{cardError}</span>
            </div> :
            null
        }
        {
          user.error && checkServerError ?
            <div aria-invalid='true' className='has-error'>
              <span className='help-block'>{fp.replace(stripeErrorPrefix)('')(user.error)}</span>
            </div> :
            null
        }
        {
          businessError && checkServerError ?
            <div aria-invalid='true' className='has-error'>
              <span className='help-block'>{fp.replace(stripeErrorPrefix)('')(businessError)}</span>
            </div> :
            null
        }
        <ControlGroup {...termsProps}>
          <ControlOption {...readTermsProps} />
          {
            isTermsValid ?
              <div aria-invalid='true' className='has-error'>
                <span id='term-validation' className='help-block' role="alert">You must accept the terms of service and privacy policy.</span>
              </div> :
              null
          }
        </ControlGroup>
        <Button type='submit' className='large' disabled={disableSubmit}>
          {
            disableSubmit ?
              <Spinner name='circle' className='spinner' fadeIn='none' /> :
              'Confirm & get started'
          }
        </Button>
        <Button bsStyle='plain' className='full' onClick={(e) => logout()}>
          Not now, I'll come back later
        </Button>
      </fieldset>
    </form>
  );

const mapStateToProps = (state) => ({
  user: state.user,
  business: businessSelector(state),
  businessError: state.business.error,
  isFetchingBusiness: state.business.isFetching,
  config: stripeConfigSelector(state),
  coupon: state.payment.coupon,
  isFetchingCoupon: state.payment.isFetchingCoupon,
});

const mapDispatchToProps = (dispatch) => ({
  logout: bindActionCreators(sessionActions.session.logout, dispatch),
  subscribeIndividualPlan: bindActionCreators(userActions.user.subscribePlan, dispatch),
  subscribeSmallTeamPlan: bindActionCreators(businessActions.business.subscribePlan, dispatch),
  getCoupon: bindActionCreators(paymentActions.payment.getCoupon, dispatch)
});

export default compose(
  withStripe,
  connect(mapStateToProps, mapDispatchToProps),
  withStateHandlers(({ selectedPlan }) => ({
    cardError: null,
    checkServerError: false,
    readTerms: false,
    submitted: false,
    isFetching: false,
    invalidUserCount: false,
    calculatedPrice: null,
    numberOfUsers: fp.getOr(1)('minQuantity')(selectedPlan),
    formRef: null,
  }), {
      setCardError: () => (error) => ({
        cardError: error ?
          error.message :
          null
      }),
      showServerError: () => () => ({
        checkServerError: true
      }),
      hideServerError: () => () => ({
        checkServerError: false
      }),
      setSubmitted: () => () => ({
        submitted: true
      }),
      setIsFetching: () => (isFetching) => ({
        isFetching
      }),
      setNumberOfUsers: (_, {
        selectedPlan: {
          maxQuantity,
          minQuantity,
          unitPrice: {
            minorAmount
          }
        }
      }) => (numberOfUsers) => ({
        numberOfUsers,
        invalidUserCount: numberOfUsers > maxQuantity || numberOfUsers < minQuantity,
        calculatedPrice: numberOfUsers ?
          (minorAmount * numberOfUsers) / 100 :
          null
      }),
      setReadTerms: () => (readTerms) => ({
        readTerms
      }),
      setFormRef: () => (ref) => ({ formRef: ref })
    }),
  withProps(({
    cardError,
    readTerms,
    invalidUserCount
  }) => ({
    isFormValid: !cardError && readTerms
  })),
  withHandlers({
    /**
     * Creates credit card payment source through Stripe and subscribe to a plan via Caddie.
     */
    createCardPayment: ({
      isFormValid,
      numberOfUsers,
      selectedPlan,
      setCardError,
      setIsFetching,
      setSubmitted,
      stripe,
      elements,
      subscribeIndividualPlan,
      subscribeSmallTeamPlan,
      user,
      nextUrl,
      hideServerError,
      formRef,
      coupon
    }) => (e) => {
      // We don't want to let default form submission happen here, which would refresh the page.
      e.preventDefault();

      // Allow validation messages (if any) to show.
      setSubmitted();

      hideServerError();

      if (isFormValid) {
        setIsFetching(true);

        const cardElements = elements.getElement('cardNumber');

        // Create stripe source token.
        stripe.createToken(cardElements).then(({ token, error }) => {
          setCardError(error);

          if (token && !error) {
            // Currently we only support one User to one Business, this may be changed in the future
            // to support multiple Businesses to one BusinessAdmin User. Only BusinessAdmin may subscribe to a Business Plan.
            const userBusinessAdminRole = fp.find(
              (roleAssignment) => roleAssignment.role === 'BusinessAdmin'
            )(user.currentUser.roleAssignments);

            if (userBusinessAdminRole && userBusinessAdminRole.businessId) {
              subscribeSmallTeamPlan({
                cardToken: token.id,
                planId: selectedPlan.id,
                businessId: userBusinessAdminRole.businessId,
                quantity: numberOfUsers,
                referralCode: cookie.load('referralCode'),
                couponId: coupon !== undefined ? coupon.id : undefined,
              }, nextUrl);
            }
            else {
              subscribeIndividualPlan({
                cardToken: token.id,
                planId: selectedPlan.id,
                quantity: 1,
                referralCode: cookie.load('referralCode'),
                couponId: coupon !== undefined ? coupon.id : undefined,
              }, nextUrl);
            }
          } else {
            setIsFetching(false);
            handleInvalidForm(formRef);
          }
        });
      } else {
        handleInvalidForm(e.target);
      }
    },
    onNumberOfUserChanges: ({ setNumberOfUsers }) =>
      (e) => setNumberOfUsers(e.value && parseInt(e.value, 10)),
    onReadTermsChange: ({ setReadTerms }) => (e) => setReadTerms(e.target.checked)
  }),
  lifecycle({
    componentWillReceiveProps({ user, businessError, isFetchingBusiness, formRef }) {
      const {
        setIsFetching,
        user: oldUser,
        isFetchingBusiness: wasFetchingBusiness,
        showServerError
      } = this.props;
      if (user.error || businessError) {
        setIsFetching(false);
      }
      if ((oldUser.isFetching && !user.isFetching) || (wasFetchingBusiness && !isFetchingBusiness)) {
        showServerError();
        if (formRef && (user.error || businessError)) {
          handleInvalidForm(formRef);
        }
      }
    }
  }),
  withProps(({
    calculatedPrice,
    numberOfUsers,
    onNumberOfUserChanges,
    onReadTermsChange,
    selectedPlan,
    submitted,
    readTerms,
    coupon
  }) => ({
    interval: selectedPlan.interval.interval,
    isTermsValid: submitted && !readTerms,
    maxUsersExceeded: numberOfUsers > selectedPlan.maxQuantity,
    price: getDiscountedPrice(coupon, calculatedPrice ?
      calculatedPrice :
      selectedPlan.minPrice.amount),
    termsProps: {
      id: 'terms',
      control: 'checkbox',
      label: null,
      required: true,
      onChange: onReadTermsChange
    },
    userCountProps: {
      id: 'user-count',
      label: null,
      value: numberOfUsers,
      required: true,
      onChange: onNumberOfUserChanges,
      min: selectedPlan.minQuantity
    }
  })),
  withProps(({
    user: {
      currentUser: {
        firstName,
        lastName,
        email
      }
    },
    business,
    userCountProps: {
      value
    }
  }) => ({
    emailBodyParams: {
      name: `${firstName} ${lastName}`,
      companyName: business ? business.name : null,
      companySize: value,
      contactEmail: email,
    }
  })),
  withProps(({
    isFetching,
    isFetchingBusiness,
    isTermsValid,
    maxUsersExceeded,
    user,
    freeTrialDays
  }) => ({
    className: classNames('signup', { enterprise: maxUsersExceeded }),
    disableSubmit: user.isFetching || isFetching || isFetchingBusiness,
    readTermsProps: {
      name: 'readTerms',
      label:
        <span>
          I've read and accept the Caddie <a href={links.thinkCaddie.terms} target='_blank'>terms and conditions</a>, which describes the terms of the {freeTrialDays} day free trial - you will not be charged by Caddie until your trial is up. I've read and accept the Caddie <a href={links.thinkCaddie.privacy} target='_blank'>privacy policy</a>.
        </span>,
      'aria-describedby': isTermsValid ? 'term-validation' : null,
      'aria-invalid': isTermsValid,
    }
  })),
  withProps(({
    userIndustryName
  }) => ({
    showCoupon: userIndustryName !== 'Financial Advice',
  }))
)(CreditCardForm);



// WEBPACK FOOTER //
// ./src/containers/pages/onboarding/credit-card-form.js